import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ImagePlus, Languages, Loader2, Sparkles, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Switch } from "../ui/switch";

// Onglet admin « Réalisations » : gestion complète des photos de la page publique
// /realisations (bannière + galerie) — ajout, suppression, légendes FR/EN, ordre,
// correction de fond par Gemini avec la clé personnelle du propriétaire
// (GEMINI_API_KEY côté serveur — jamais exposée au navigateur).

// Fonds tout prêts (demande du propriétaire) : un clic lance la correction —
// la commande libre reste disponible juste en dessous.
const FOND_PRESETS = [
  { id: "blanc", label: "Blanc studio",
    instruction: "Fond blanc studio propre, lumineux et uniforme, avec un léger ombrage naturel sous le sujet" },
  { id: "atelier", label: "Atelier sombre doré",
    instruction: "Fond d'atelier sombre et chaleureux avec des reflets dorés élégants, ambiance prestige" },
  { id: "gala", label: "Gala",
    instruction: "Fond de gala festif : rideau sombre, lumières dorées tamisées et bokeh élégant" },
];

function PhotoCard({ p, onSave, onMove, onDelete, onFond, onFondSubmit, fondOpen, fondText, setFondText, setFondOpen, saving, geminiReady,
                     customPresets, onDeletePreset, presetLabel, setPresetLabel, onSavePreset, onCaptionAI, aiCaption }) {
  const [fr, setFr] = useState(p.caption_fr || "");
  const [en, setEn] = useState(p.caption_en || "");
  const processing = p.fond_status === "processing";
  return (
    <div className="border border-ivory/10 rounded-xl overflow-hidden bg-ink/40" data-testid={`real-card-${p.id}`}>
      <div className="relative">
        <img src={p.url} alt={fr || "photo"} className="w-full h-44 object-cover" />
        {p.kind === "banner" && (
          <span className="absolute top-2 left-2 bg-gold text-ink text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">Bannière</span>
        )}
        {processing && (
          <span className="absolute inset-0 bg-ink/70 flex flex-col items-center justify-center gap-2 text-gold text-xs">
            <Loader2 className="w-6 h-6 animate-spin" /> Gemini corrige le fond…
          </span>
        )}
      </div>
      <div className="p-4 space-y-2.5">
        <input value={fr} onChange={(e) => setFr(e.target.value)} placeholder="Légende française"
          data-testid={`real-caption-fr-${p.id}`}
          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
        <input value={en} onChange={(e) => setEn(e.target.value)} placeholder="Légende anglaise"
          data-testid={`real-caption-en-${p.id}`}
          className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
        <div className="flex items-center justify-between gap-2 pt-1">
          <div className="flex items-center gap-1.5">
            <button onClick={() => onMove(p, -1)} data-testid={`real-up-${p.id}`} title="Monter"
              className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-ivory/15 text-ivory/70 hover:border-gold hover:text-gold transition-colors">
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onMove(p, 1)} data-testid={`real-down-${p.id}`} title="Descendre"
              className="w-8 h-8 inline-flex items-center justify-center rounded-full border border-ivory/15 text-ivory/70 hover:border-gold hover:text-gold transition-colors">
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] ${p.active ? "text-green-400" : "text-ivory/65"}`}>{p.active ? "Visible" : "Masquée"}</span>
            <Switch checked={!!p.active} onCheckedChange={(v) => onSave(p, { active: v })} data-testid={`real-active-${p.id}`} />
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          <button onClick={() => onSave(p, { caption_fr: fr, caption_en: en })} disabled={saving === p.id}
            data-testid={`real-save-${p.id}`}
            className="text-[11px] border border-ivory/25 text-ivory/80 px-3 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
            Enregistrer
          </button>
          {p.kind !== "banner" && (
            <button onClick={() => onSave(p, { kind: "banner" })} data-testid={`real-banner-${p.id}`}
              className="text-[11px] border border-ivory/25 text-ivory/80 px-3 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
              Définir bannière
            </button>
          )}
          <button onClick={() => onFond(p)} disabled={!geminiReady || processing}
            data-testid={`real-fond-open-${p.id}`}
            title={geminiReady ? "Corriger le fond avec Gemini" : "Clé Gemini non configurée"}
            className="text-[11px] inline-flex items-center gap-1 bg-gold/15 border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
            <Sparkles className="w-3 h-3" /> Corriger le fond
          </button>
          {/* Gemini regarde la photo et écrit la légende FR + sa traduction EN */}
          <button onClick={() => onCaptionAI(p)} disabled={!geminiReady || aiCaption === p.id}
            data-testid={`real-caption-ai-${p.id}`}
            title={geminiReady ? "Gemini commente la photo et traduit la légende" : "Clé Gemini non configurée"}
            className="text-[11px] inline-flex items-center gap-1 bg-gold/15 border border-gold/40 text-gold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors disabled:opacity-50">
            {aiCaption === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
            Commenter + traduire
          </button>
          <button onClick={() => onDelete(p)} data-testid={`real-delete-${p.id}`}
            className="text-[11px] inline-flex items-center gap-1 border border-red-400/40 text-red-300 px-3 py-1.5 rounded-full hover:bg-red-400 hover:text-ink transition-colors">
            <Trash2 className="w-3 h-3" /> Retirer
          </button>
        </div>
        {p.fond_instruction && (
          <p className="text-[10px] text-ivory/45 pt-1">Dernier fond demandé : « {p.fond_instruction} »</p>
        )}
        {p.fond_status === "failed" && p.fond_error && (
          <p className="text-[10px] text-red-300/90 pt-1" data-testid={`real-fond-error-${p.id}`}>{p.fond_error}</p>
        )}
        {fondOpen && (
          <div className="border border-gold/25 rounded-lg p-3 space-y-2 bg-gold/5" data-testid={`real-fond-panel-${p.id}`}>
            <p className="text-[11px] text-gold font-semibold">Fonds tout prêts — un clic et c'est parti :</p>
            <div className="flex flex-wrap gap-1.5">
              {FOND_PRESETS.map((pr) => (
                <button key={pr.id} onClick={() => onFondSubmit(p, pr.instruction)}
                  data-testid={`real-fond-preset-${pr.id}-${p.id}`}
                  className="text-[11px] bg-gold/15 border border-gold/40 text-gold font-semibold px-3 py-1.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
                  {pr.label}
                </button>
              ))}
              {/* Fonds favoris du propriétaire (illimités) — × pour retirer */}
              {customPresets.map((pr) => (
                <span key={pr.id} className="inline-flex items-center gap-1 text-[11px] bg-champagne/15 border border-champagne/50 text-champagne font-semibold pl-3 pr-1.5 py-1 rounded-full">
                  <button onClick={() => onFondSubmit(p, pr.instruction)}
                    data-testid={`real-fond-custom-${pr.id}-${p.id}`}
                    className="hover:text-ink hover:bg-champagne rounded-full px-1 transition-colors">
                    {pr.label}
                  </button>
                  <button onClick={() => onDeletePreset(pr)} title="Retirer ce favori"
                    data-testid={`real-fond-custom-del-${pr.id}-${p.id}`}
                    className="text-champagne/60 hover:text-red-300 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <p className="text-[11px] text-gold font-semibold pt-1">Ou décrivez le fond souhaité :</p>
            <textarea value={fondText} onChange={(e) => setFondText(e.target.value)} rows={2}
              placeholder="Ex. : fond bleu hockey, salle de billard, scène de gala…"
              data-testid={`real-fond-instruction-${p.id}`}
              className="w-full bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none resize-none" />
            <div className="flex gap-2">
              <button onClick={() => onFondSubmit(p)} data-testid={`real-fond-submit-${p.id}`}
                className="text-[11px] bg-gold text-ink font-semibold px-4 py-1.5 rounded-full hover:bg-champagne transition-colors">
                Lancer la correction
              </button>
              <button onClick={() => setFondOpen(null)} data-testid={`real-fond-cancel-${p.id}`}
                className="text-[11px] border border-ivory/25 text-ivory/70 px-3 py-1.5 rounded-full hover:border-gold hover:text-gold transition-colors">
                Annuler
              </button>
            </div>
            {/* Enregistrer la commande libre comme fond favori réutilisable (illimité) */}
            <div className="flex gap-1.5 pt-1">
              <input value={presetLabel} onChange={(e) => setPresetLabel(e.target.value)}
                placeholder="Nom du favori (ex. : Bleu hockey)"
                data-testid={`real-fond-fav-name-${p.id}`}
                className="flex-1 min-w-0 bg-ink border border-ivory/15 rounded-lg px-3 py-1.5 text-[11px] focus:border-gold focus:outline-none" />
              <button onClick={() => onSavePreset(p)} data-testid={`real-fond-fav-save-${p.id}`}
                className="text-[11px] inline-flex items-center gap-1 border border-champagne/50 text-champagne px-3 py-1.5 rounded-full hover:bg-champagne hover:text-ink transition-colors">
                <Star className="w-3 h-3" /> Garder en favori
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RealisationsTab() {
  const [photos, setPhotos] = useState([]);
  const [geminiReady, setGeminiReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("photo");
  const [capFr, setCapFr] = useState("");
  const [capEn, setCapEn] = useState("");
  const [adding, setAdding] = useState(false);
  const [fondId, setFondId] = useState(null);
  const [fondText, setFondText] = useState("");
  const [saving, setSaving] = useState("");
  const [customPresets, setCustomPresets] = useState([]);  // fonds favoris du propriétaire (illimités)
  const [presetLabel, setPresetLabel] = useState("");
  const [aiCaption, setAiCaption] = useState("");          // photo en cours de commentaire/traduction IA
  const prevStatus = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/realisation-photos");
      const list = data.photos || [];
      // Toasts de fin de correction (sondage tant qu'un traitement Gemini est en cours)
      list.forEach((p) => {
        const prev = prevStatus.current[p.id];
        if (prev === "processing" && p.fond_status === "done") toast.success("Fond corrigé par Gemini — la photo est à jour");
        if (prev === "processing" && p.fond_status === "failed") toast.error("La correction de fond a échoué — réessayez avec une autre description");
        prevStatus.current[p.id] = p.fond_status || "";
      });
      setPhotos(list);
      setGeminiReady(!!data.gemini_ready);
      setCustomPresets(data.custom_presets || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Sondage : une correction Gemini dure 30 à 60 s — on rafraîchit jusqu'au résultat
  useEffect(() => {
    if (!photos.some((p) => p.fond_status === "processing")) return undefined;
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [photos]);  // eslint-disable-line react-hooks/exhaustive-deps

  const addPhoto = async () => {
    if (!file) { toast.error("Choisissez une image d'abord"); return; }
    setAdding(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const up = await api.post("/admin/uploads/image", form);
      await api.post("/admin/realisation-photos", {
        url: up.data.url, kind, caption_fr: capFr, caption_en: capEn,
      });
      toast.success("Photo ajoutée à la page Réalisations");
      setFile(null); setCapFr(""); setCapEn(""); setKind("photo");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  const savePhoto = async (p, fields) => {
    setSaving(p.id);
    try {
      await api.patch(`/admin/realisation-photos/${p.id}`, fields);
      toast.success("Enregistré");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving("");
    }
  };

  const move = async (p, direction) => {
    await api.post(`/admin/realisation-photos/${p.id}/move`, { direction }).catch(() => {});
    await load();
  };

  const removePhoto = async (p) => {
    if (!window.confirm("Retirer cette photo de la page Réalisations ? Cette action est définitive.")) return;
    try {
      await api.delete(`/admin/realisation-photos/${p.id}`);
      toast.success("Photo retirée");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Fonds favoris (illimités) : enregistrés dans les réglages de la boutique
  const savePreset = async (p) => {
    const label = presetLabel.trim();
    const instruction = fondText.trim();
    if (!label) { toast.error("Donnez un nom au favori (ex. : Bleu hockey)"); return; }
    if (instruction.length < 3) { toast.error("Décrivez d'abord le fond dans le champ de commande"); return; }
    const next = [...customPresets, { id: Date.now().toString(36), label: label.slice(0, 40), instruction: instruction.slice(0, 300) }];
    try {
      await api.patch("/admin/settings", { realisation_fond_presets: next });
      setCustomPresets(next);
      setPresetLabel("");
      toast.success(`Favori « ${label} » enregistré — il est dans les fonds tout prêts`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deletePreset = async (pr) => {
    if (!window.confirm(`Retirer le fond favori « ${pr.label} » ?`)) return;
    const next = customPresets.filter((x) => x.id !== pr.id);
    try {
      await api.patch("/admin/settings", { realisation_fond_presets: next });
      setCustomPresets(next);
      toast.success("Favori retiré");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  // Gemini regarde la photo et écrit la légende FR + sa traduction EN (enregistrées
  // directement — retouchables dans les champs ensuite)
  const captionAI = async (p) => {
    setAiCaption(p.id);
    try {
      const { data } = await api.post(`/admin/realisation-photos/${p.id}/legende-ia`);
      toast.success(`Légende écrite et traduite : « ${(data.caption_fr || "").slice(0, 80)} »`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiCaption("");
    }
  };

  const submitFond = async (p, presetInstruction) => {
    // presetInstruction : instruction d'un fond tout prêt (un clic) ; sinon la commande libre
    const text = (presetInstruction ?? fondText).trim();
    if (text.length < 3) { toast.error("Décrivez le fond souhaité"); return; }
    if (!window.confirm(`Corriger le fond de cette photo avec Gemini ?\n\nInstruction : « ${text} »\n\nLa photo retouchée remplacera l'actuelle (30 à 60 secondes).`)) return;
    try {
      await api.post(`/admin/realisation-photos/${p.id}/fond`, { instruction: text });
      toast.success("Correction lancée — Gemini travaille (30 à 60 s)");
      setFondId(null); setFondText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="realisations-tab">
      <div>
        <h2 className="font-serif text-2xl text-champagne">Photos de la page Réalisations</h2>
        <p className="text-xs text-ivory/65 mt-1">
          Ajoutez, retirez et ordonnez les photos de mrtrophee.ca/realisations. Le bouton « Corriger le fond »
          envoie la photo à Gemini (votre compte Google) avec votre description — la photo retouchée remplace l'actuelle.
          {!geminiReady && <span className="block text-amber-300 mt-1">Clé Gemini non configurée côté serveur (GEMINI_API_KEY) — la correction de fond est désactivée.</span>}
        </p>
      </div>

      {/* Ajout d'une photo */}
      <div className="border border-dashed border-gold/30 rounded-xl p-5 space-y-3 bg-gold/5" data-testid="real-add-form">
        <p className="text-sm font-semibold text-gold inline-flex items-center gap-2"><ImagePlus className="w-4 h-4" /> Ajouter une photo</p>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="real-add-file"
            className="text-xs text-ivory/70 file:mr-3 file:bg-gold file:text-ink file:font-semibold file:border-0 file:px-4 file:py-2 file:rounded-full file:cursor-pointer" />
          <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="real-add-kind"
            className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none">
            <option value="photo">Photo de galerie</option>
            <option value="banner">Bannière du haut</option>
          </select>
        </div>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <input value={capFr} onChange={(e) => setCapFr(e.target.value)} placeholder="Légende française (optionnel)"
            data-testid="real-add-caption-fr"
            className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
          <input value={capEn} onChange={(e) => setCapEn(e.target.value)} placeholder="Légende anglaise (optionnel)"
            data-testid="real-add-caption-en"
            className="bg-ink border border-ivory/15 rounded-lg px-3 py-2 text-xs focus:border-gold focus:outline-none" />
        </div>
        <button onClick={addPhoto} disabled={adding} data-testid="real-add-submit"
          className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-2.5 rounded-full hover:bg-champagne transition-colors disabled:opacity-60 text-sm">
          {adding && <Loader2 className="w-4 h-4 animate-spin" />} Ajouter à la page
        </button>
      </div>

      {loading ? (
        <p className="text-ivory/65 text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {photos.map((p) => (
            <PhotoCard key={`${p.id}-${p.updated_at}`} p={p}
              onSave={savePhoto} onMove={move} onDelete={removePhoto}
              onFond={(ph) => { setFondId(ph.id); setFondText(""); }}
              onFondSubmit={submitFond}
              fondOpen={fondId === p.id} fondText={fondText} setFondText={setFondText} setFondOpen={setFondId}
              saving={saving} geminiReady={geminiReady}
              customPresets={customPresets} onDeletePreset={deletePreset}
              presetLabel={presetLabel} setPresetLabel={setPresetLabel} onSavePreset={savePreset}
              onCaptionAI={captionAI} aiCaption={aiCaption} />
          ))}
        </div>
      )}
    </div>
  );
}
